import React, { useState } from "react";
import {
  Newspaper,
  Settings,
  Mail,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import Logo from "../../Assets/black-logo.png";
import { href, useLocation, useNavigate } from "react-router-dom";
import { retrieveData } from "../../LocalConnection/LocalConnection.js";
const Sidebar = ({ collapsed: desktopCollapsed, mobileOpen, setMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userRole = retrieveData("lazyx_admin_role");
  const [openParent, setOpenParent] = useState(null);

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 768;

  // 🔑 Mobile = always expanded
  const isCollapsed = isMobile ? false : desktopCollapsed;

  const toggleParent = (id) => {
    setOpenParent(openParent === id ? null : id);
  };

  const handleNavigation = (href) => {
    if (href) {
      navigate(href);
      setMobileOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };
  

/* ===============================
     🔥 ALL MENU ITEMS
  ================================ */
  const allMenuItems = [
    {
      id: "bookings",
      label: "Bookings",
      icon: Newspaper,
      href: "/bookings",
      children: [],
    },
    {
      id: "scanpass",
      label: "Scanpass",
      icon: Newspaper,
      href: "/scanpass",
      children: [],
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: Newspaper,
      href: "/analytics",
      children: [],
    },
    {
      id: "orders",
      label: "Orders",
      icon: Newspaper,
      href: "/orders",
      children: [
        { id: "orders", label: "Orders", href: "/orders" },
        { id: "abandoneorders", label: "Abandone Orders", href: "/abandoneorders" },
      ],
    },
    {
      id: "allusers",
      label: "All Users",
      icon: Newspaper,
      href: "/all-users",
      children: [],
    },
    {
      id: "products",
      label: "Products",
      icon: Settings,
      href: "/allproducts",
      children: [
        { id: "productslist", label: "Products List", href: "/allproducts" },
        { id: "collection", label: "Collection", href: "/collection" },
      ],
    },
    {
      id: "pod",
      label: "POD",
      icon: Settings,
      href: "/pod",
      children: [],
    },
    {
      id: "contact",
      label: "Contact Messages",
      icon: Mail,
      href: "/contacts",
      children: [],
    },
  ];

  /* ===============================
     🔐 ROLE BASED ACCESS
  ================================ */
  const ROLE_ACCESS = {
    admin: allMenuItems.map((item) => item.id),
    staff: ["bookings", "scanpass"],
  };

  const menuItems = allMenuItems.filter((item) =>
    ROLE_ACCESS[userRole]?.includes(item.id)
  );
  return (
    <>
      {/* Mobile Hamburger */}

      {!mobileOpen && (
      <button
        onClick={toggleMobileMenu}
        className="fixed top-3 left-2 z-50 md:hidden bg-white rounded-lg p-2"
      >
        <Menu className="h-6 w-6" />
      </button>
      )}

      {/* Overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 h-screen bg-white border-r transition-transform duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static
          ${isCollapsed ? "md:w-[78px]" : "md:w-[260px]"}
          w-[280px]
        `}
      >
        <div className="flex h-full flex-col">
          {/* 🔥 Mobile Header */}
          <div className="md:hidden flex items-center justify-between px-4 h-16 border-b">
            <span className="text-lg font-semibold">Menu</span>
            <button onClick={toggleMobileMenu}>
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Desktop Header */}
          <div
            className={`hidden w-ful md:flex md:h-16 items-center border-b justify-center `}
          >
            <img
              src={Logo}
              alt="logo"
              className={`${isCollapsed?"w-[55%]" : "w-[28%] p-2"} `}
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            <ul className="flex flex-col gap-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isParentActive =
                  item.href && location.pathname.startsWith(item.href);
                const hasActiveChild = item.children.some(
                  (c) => location.pathname.startsWith(c.href)
                );

                const isOpen =
                  openParent === item.id || isParentActive || hasActiveChild;

                return (
                  <li key={item.id}>
                    <div
                      onClick={() =>
                        item.children.length
                          ? toggleParent(item.id)
                          : handleNavigation(item.href)
                      }
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer
                        ${
                          isParentActive || hasActiveChild
                            ? "bg-gray-100"
                            : "hover:bg-gray-100"
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        {!isCollapsed && (
                          <span className="text-sm font-medium">
                            {item.label}
                          </span>
                        )}
                      </div>

                      {!isCollapsed &&
                        item.children.length > 0 &&
                        (isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        ))}
                    </div>

                    {!isCollapsed &&
                      isOpen &&
                      item.children.length > 0 && (
                        <ul className="ml-10 mt-1 border-l pl-3 flex flex-col gap-1">
                          {item.children.map((child) => (
                            <li key={child.id}>
                              <div
                                onClick={() =>
                                  handleNavigation(child.href)
                                }
                                className={`px-2 py-1.5 rounded-md text-sm cursor-pointer
                                  ${
                                    location.pathname.startsWith(child.href)
                                      ? "bg-[var(--primary-color)] text-white"
                                      : "hover:bg-gray-100"
                                  }
                                `}
                              >
                                {child.label}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
